import { layoutStyle } from '@/utils/constants';

export default {
  namespaced: true,
  state: {
    layoutStyle: 1,
    materialSwitch: 'chat',
    layoutColor: 'light',
    isVideoFullScreen: false,
    isMaterialFullScreen: false,
    isMobileSize: false,
    isRemoteFitted: false,
    isLocalFitted: false,
    isVideoReloading: false,
    sliderLayout: null,
    sliderPdfWidth: null,
    isIpadDevice: false,
    videoOverlayActivated: false,
    isMenuVisible: null,
    isVocabularyVisible: false,
    isVocabularyDeleteVisible: false,
    sliderPdfHtmlWidth: null,
    viewportHeight: window.innerHeight,
    materialViewportHeight: window.innerHeight,
  },
  getters: {
    /**
     * Current visible viewport height in pixels
     *
     * Single source of truth for lesson-room height computations that
     * should always track the live visible height (chat, video). Kept in
     * sync with the `--app-vh` CSS custom property by the viewport tracker
     * (see src/plugin/VueViewportPlugin.js). Always live — never frozen.
     *
     * @return number
     */
    viewportHeight: state => {
      return state.viewportHeight;
    },

    /**
     * Visible viewport height used for material sizing (PDF/HTML/Discovery).
     *
     * Mirrors `viewportHeight` except while a text input is focused (the
     * on-screen keyboard is presumed open), when it freezes at its
     * pre-keyboard value so the material panel doesn't collapse from the
     * mobile branch's double subtraction of the video column height. Kept
     * in sync with the `--app-vh-material` CSS custom property.
     * See .kiro/specs/lesson-room-viewport-keyboard.
     *
     * @return number
     */
    materialViewportHeight: state => {
      return state.materialViewportHeight;
    },

    /**
     * Check if current layout is video/screen streaming
     *
     * @return boolean
     */
    isVideoScreenLayout: state => {
      return state.layoutStyle === layoutStyle.videoScreen
    },

    /**
     * Check if current layout is default
     *
     * @return boolean
     */
    isDefaultLayout: state => {
      return state.layoutStyle === layoutStyle.default
    },

    /**
     * Check if current layout is material display
     *
     * @return boolean
     */
    isMaterialLayout: state => {
      return state.layoutStyle === layoutStyle.material
    },

    /**
     * Check if slider layout is using material display
     *
     * @return boolean
     */
    isSliderInLayout2: state => {
      return state.sliderLayout === layoutStyle.material
    },

    /**
     * Check if current Color is toggled
     *
     * @return boolean
     */
    isColorSchemeDark: state => {
      return state.layoutColor === 'dark';
    },

    /**
     * Check if trainer is accessing layout (1) - Display Student Material
     *
     * @return boolean
     */
    isTrainerLayout1View: (state, getters, rootState, rootGetters) => {
      return getters.isDefaultLayout && rootGetters.isTrainer;
    }
  },
  mutations: {
    setLayoutStyle(state, payload) {
      state.layoutStyle = payload;
    },
    setSliderLayout(state, payload) {
      state.sliderLayout = payload;
    },
    setMaterialState(state, data) {
      state.materialSwitch = data;
      state.isVideoFullScreen = false;
      state.isMaterialFullScreen = false;
    },
    videoFullScreen(state) {
      state.isVideoFullScreen = !state.isVideoFullScreen;
    },
    materialFullScreen(state) {
      state.isMaterialFullScreen = !state.isMaterialFullScreen;
    },
    setLayoutColor(state, payload) {
      state.layoutColor = payload;
    },
    setIsMobileSize(state, payload) {
      state.isMobileSize = payload;
    },
    setIsRemoteFitted(state, payload) {
      state.isRemoteFitted = payload;
    },
    setIsLocalFitted(state, payload) {
      state.isLocalFitted = payload;
    },
    setIsVideoReloading(state, payload) {
      state.isVideoReloading = payload;
    },
    setSliderPdfWidth(state, payload) {
      state.sliderPdfWidth = payload;
    },
    setSliderPdfHtmlWidth(state, payload) {
      state.sliderPdfHtmlWidth = payload;
    },
    setIsIpadDevice(state, payload) {
      state.isIpadDevice = payload;
    },
    setVideoOverlayActivated(state, payload) {
      state.videoOverlayActivated = payload;
    },
    setIsMenuVisible(state, payload) {
      state.isMenuVisible = payload;
    },
    setIsVocabularyVisible(state, payload) {
      state.isVocabularyVisible = payload;
    },
    setIsVocabularyDeleteVisible(state, payload) {
      state.isVocabularyDeleteVisible = payload;
    },
    setViewportHeight(state, payload) {
      state.viewportHeight = payload;
    },
    setMaterialViewportHeight(state, payload) {
      state.materialViewportHeight = payload;
    }
  },
  actions: {
    /**
     * Switch Layout
     *
     * @param {commit} commit
     * @return void
     */
    switchLayoutStyle({ commit }, layout) {
      commit('setLayoutStyle', layout);
    },

    /**
     * Switch Slider Layout Between Layout 1 and 2
     *
     * @param {commit} commit
     * @return void
     */
    switchSliderLayout({ commit, state }, layout) {
      if (layout !== state.sliderLayout) {
        commit('setSliderLayout', layout);
      }
    },

    /**
     * Switch Material State
     *
     * @param {commit} commit
     * @return void
     */
    switchMaterialState({ commit }, layout) {
      commit('setMaterialState', layout);
    },

    /**
     * Switch Layout Color
     *
     * @param {commit} commit
     * @return void
     */
    switchLayoutColor({ commit }, layout) {
      commit('setLayoutColor', layout);
    },

    /**
     * Check if screen size is for mobile/pc design
     *
     * @param {commit} commit
     * @return void
     */
    checkMediaSize({ commit }) {
      let isMediaSetBreakPoint = window.innerWidth < 960;
      commit('setIsMobileSize', isMediaSetBreakPoint);
    },

    /**
     * Check the device if it's an iPad
     *
     * @param {commit} commit
     * @return void
     */
    checkIfIpadDevice({ commit }) {
      // using ua-parser library detects iPad with version 13+ identical with MacOS computer
      const isIpad = !!(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isOldIpad = (/iPad/.test(navigator.platform) && navigator.maxTouchPoints > 2);
      commit('setIsIpadDevice', isIpad || isOldIpad);
    }
  }
};
